import * as SignalR from '@microsoft/signalr';
import { IAppContext } from 'App/AppContext';
import { apiRequest } from 'services/Auth/authConfig';
import Config from 'services/Config/configService';
import logger from 'services/Logging/logService';

//
// A SignalR connection to a hub of the API, ported from ISOPlanner's GeneralHub. WebSockets only; the token goes in
// the access_token query string (a browser cannot set a header on a WebSocket). SkillControl reads the tenant from
// the token, so the hub url carries no org unit.
//

export interface IHubMethod {
  method: string;
  callback: unknown;
}

export interface IHubConnection {
  readonly connectionId: string | null;
  connection: SignalR.HubConnection;
}

const SendConnectionId = 'SendConnectionId';

// Reconnect at 0s, 2s, 5s, 10s, 30s, then once every 60s up to ~5 min total.
const reconnectSchedule = [0, 2000, 5000, 10000, 30000, 60000, 60000, 60000];

const getServerConnectionId = async (connection: SignalR.HubConnection): Promise<string | null> => {
  try {
    return await connection.invoke<string>(SendConnectionId);
  } catch (err) {
    logger.debug('Error getting hub connection id', err);

    return null;
  }
};

export const startHubConnection = async (
  appContext: IAppContext,
  hubUrl: string,
  methods: IHubMethod[],
): Promise<IHubConnection | null> => {
  try {
    if (!appContext.user?.login?.tenantId) {
      logger.debug('startHubConnection: user tenantId not available, skipping connect');

      return null;
    }

    const baseUrl = Config.get('Api.BaseURL');
    const newConnection = new SignalR.HubConnectionBuilder()
      .configureLogging(SignalR.LogLevel.Error)
      .withUrl(`${baseUrl}/${hubUrl}`, {
        skipNegotiation: true,
        transport: SignalR.HttpTransportType.WebSockets,
        // Pass the function reference so the token is fetched again on every retry; capturing the resolved
        // value here hands out the same token forever and reconnects 401 once it expires.
        accessTokenFactory: () => appContext.getAccessToken(apiRequest.scopes),
      })
      .withAutomaticReconnect(reconnectSchedule)
      .build();

    for (const { method, callback } of methods) {
      newConnection.on(method, callback as never);
    }
    await newConnection.start();

    let serverConnectionId: string | null = newConnection.connectionId ?? (await getServerConnectionId(newConnection));
    newConnection.onreconnected(async (connectionId) => {
      serverConnectionId = connectionId ?? newConnection.connectionId ?? (await getServerConnectionId(newConnection));
    });

    return {
      // Live getter: SignalR assigns a new id on each reconnect; a cached value would silently go stale.
      get connectionId() {
        return newConnection.connectionId ?? serverConnectionId;
      },
      connection: newConnection,
    };
  } catch (err) {
    // allowed to fail, e.g. a proxy blocking websockets: callers handle null and run without streaming updates
    logger.debug('Error starting hub connection', err);

    return null;
  }
};

export const stopHubConnection = async (
  hubConnection: SignalR.HubConnection | IHubConnection | null | undefined,
): Promise<void> => {
  try {
    if (hubConnection) {
      const connection = hubConnection instanceof SignalR.HubConnection ? hubConnection : hubConnection.connection;
      // a connection that is starting or reconnecting is stopped too, so it does not outlive its owner
      if (connection.state !== SignalR.HubConnectionState.Disconnected) {
        await connection.stop();
      }
    }
  } catch {
    //swallow error
  }
};
